import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import type { AcademicAnalyticsResult } from '@dosfilos/application';
import { graduationCandidatesApi } from '../../../../../lib/api/graduation-candidates.api';
import { callable } from '../../../../../lib/callable';
import { annexPeopleIds, buildAnnex, peopleRequests, type PersonName } from '../named-lists.model';
import { db } from '../../../../../lib/firebase';
import { errorText } from '../../../../../lib/error-text';
import { formatDateTime, formatNumber } from '../../../../../lib/i18n-format';
import { openGeneratedPdf } from '../../../../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../../../../lib/pdf-hyphenation';
import { useTenantStore } from '../../../../../stores/tenant.store';
import { pickDocumentLogo } from '../../academic-record/academic-record.pdf';
import { GAP_BUCKETS, type GapBucket } from '../analytics-charts.model';
import type { AnalyticsPdfData, AnalyticsPdfLabels } from './AnalyticsPdf';
import type { PdfBrand } from './pdf-kit';

// ─── Exportar el panel: diapositivas 16:9 o informe carta ───────────────────
// Con los MISMOS modelos que la pantalla (no recalcula nada) y lo que está
// elegido en ella. La pestaña se abre dentro del clic (openGeneratedPdf).

const FALLBACK_COLOR = '#2c4f86';

const getAnalyticsPeople = callable<
  { tenantId: string; personIds: string[] },
  { people: PersonName[] }
>('getAnalyticsPeople');

export interface AnalyticsExportInput {
  result: AcademicAnalyticsResult;
  courses: ReadonlyMap<string, { code: string; name: string }>;
  data: AnalyticsPdfData;
  range: { from: number; to: number };
  programName: string | null;
  site: string | null;
  near: number;
  startYear: number;
}

/** La marca del seminario para un PDF (logo de documentos y color principal). */
export async function loadPdfBrand(tenantId: string): Promise<PdfBrand> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}`));
  const tenant = (snap.data() ?? {}) as Record<string, unknown>;
  const config = (tenant['config'] ?? {}) as Record<string, unknown>;
  const logoUrl = pickDocumentLogo(config);
  return {
    tenantName: (tenant['name'] as string | undefined) ?? '',
    ...(logoUrl ? { logoUrl } : {}),
    primaryColor:
      typeof config['primaryColor'] === 'string' && config['primaryColor']
        ? (config['primaryColor'] as string)
        : FALLBACK_COLOR,
  };
}

export function useAnalyticsExport() {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [error, setError] = useState<string | null>(null);
  const a = (key: string, o?: Record<string, unknown>) => t(`reports.analytics.${key}`, o ?? {});

  const labels = (input: AnalyticsExportInput, brand: PdfBrand): AnalyticsPdfLabels => {
    const { result: r, data } = input;
    const minN = r.params.minN;
    const pct = (v: number) => formatNumber(v, { style: 'percent', maximumFractionDigits: 0 });
    const rateName = a(`distribution.rateName.${r.alertRateKind}`);
    const threshold = pct(r.params.alertRateThreshold);
    const snapshot = a('export.snapshot', {
      date: formatDateTime(r.dataAsOf, { dateStyle: 'long', timeStyle: 'short' }),
    });
    const pts = data.series.points;
    const before = a('cohorts.before', { year: input.startYear });
    const passKeys = (['A', 'B', 'C', 'D', 'other'] as const).filter(
      (s) => !(s === 'D' && r.params.dCountsInAlertRate),
    );
    const alertKeys = r.params.dCountsInAlertRate
      ? (['D', 'F', 'W'] as const)
      : (['F', 'W'] as const);
    return {
      title: a('export.title'),
      tenantLine: brand.tenantName,
      filters: [
        a('export.filters.range', { from: input.range.from, to: input.range.to }),
        a('export.filters.program', { name: input.programName ?? a('filters.allPrograms') }),
        a('export.filters.site', { name: input.site ?? a('filters.allSites') }),
        a('export.filters.near', { count: input.near }),
      ],
      snapshot,
      page: (n, total) => a('export.page', { n, total }),
      pct,
      suppressed: a('suppressed', { min: minN }),
      suppressedLong: a('suppressedLong', { min: minN }),
      n: (n) => a('n', { count: n }),
      series: {
        title: a('series.title'),
        definition: a('series.definition'),
        meta: pts.length
          ? a('series.n', {
              min: Math.min(...pts.map((p) => p.active)),
              max: Math.max(...pts.map((p) => p.active)),
            })
          : '',
        foot: [
          ...(data.series.before !== null
            ? [a('series.before', { year: input.startYear, count: data.series.before })]
            : []),
          a('series.foot'),
        ],
        active: a('series.active'),
        persistence: a('series.persistence'),
        pending: a('series.pending'),
      },
      distribution: {
        title: a('distribution.title'),
        definition: a('distribution.definition', { rate: rateName }),
        meta: `${a('distribution.n')} · ${a('distribution.order')}`,
        foot: [a('distribution.foot', { rate: rateName, threshold })],
        rateName,
        legend: `${a('distribution.pass')}: ${passKeys.map((k) => a(`distribution.segment.${k}`)).join(', ')} · ${rateName}: ${alertKeys
          .map((k) => a(`distribution.segment.${k}`))
          .join(', ')} · ▲ ${a('distribution.flag', { rate: rateName, threshold })}`,
        extra: (aud, legacy) => a('distribution.extra', { aud, legacy }),
        continued: a('export.continued'),
      },
      cohorts: {
        title: a('cohorts.title'),
        definition: a('cohorts.definition'),
        meta: a('cohorts.n'),
        foot: data.cohorts
          ? [
              a('cohorts.foot', {
                horizon: data.cohorts.horizon,
                late: data.cohorts.graduatedAfterHorizon,
                none: data.cohorts.withoutCourses,
              }),
            ]
          : [],
        x: a('cohorts.x'),
        ref: data.cohorts ? a('cohorts.ref', { count: data.cohorts.normalTerms }) : '',
        legend: (key, from, to) => (key === 'before' ? before : `${from}–${to}`),
        status: (i) => {
          const l = data.cohorts!.lines[i]!;
          const st = !l.points
            ? a('suppressedLong', { min: minN })
            : l.complete
              ? a('cohorts.complete')
              : a('cohorts.incomplete', { k: l.observedTerms });
          return `${a('n', { count: l.size })} · ${st}`;
        },
        notConfigured: a('cohorts.notConfigured'),
      },
      annex: {
        title: a('export.annex.title'),
        pii: t('reports.candidates.letter.pii'),
        note: a('export.annex.note', {
          filters: [
            brand.tenantName,
            a('export.filters.range', { from: input.range.from, to: input.range.to }),
            a('export.filters.program', { name: input.programName ?? a('filters.allPrograms') }),
            a('export.filters.site', { name: input.site ?? a('filters.allSites') }),
          ].join(' · '),
        }),
        nearTitle: a('lists.near.title'),
        gapTitle: (bucket, count) =>
          `${a('lists.gap.title')} · ${a(`candidates.gaps.${bucket}`)} · ${a('lists.total', { count })}`,
        repeatTitle: a('lists.repeat.title'),
        cols: {
          name: a('lists.cols.name'),
          code: a('lists.cols.code'),
          missing: a('lists.cols.missing'),
          which: a('lists.cols.which'),
          lastTerm: a('lists.cols.lastTerm'),
          course: a('lists.cols.course'),
          attempts: a('lists.cols.attempts'),
        },
        which: (x) =>
          [
            x.requiredMissing.join(', '),
            x.groupShortfall ? a('lists.near.core', { count: x.groupShortfall }) : '',
          ]
            .filter(Boolean)
            .join(' · ') || '—',
        total: (n) => a('lists.total', { count: n }),
      },
      candidates: {
        title: a('candidates.title'),
        definition: a('candidates.definition', { count: input.near }),
        meta: data.candidates ? a('candidates.n', { count: data.candidates.total }) : '',
        foot: data.candidates
          ? [
              a('candidates.foot'),
              ...(data.candidates.withoutGap > 0
                ? [a('candidates.withoutGap', { count: data.candidates.withoutGap })]
                : []),
            ]
          : [],
        missing: (k) => (k === 0 ? a('candidates.zero') : a('candidates.row', { count: k })),
        cross: a('candidates.cross'),
        gaps: Object.fromEntries(GAP_BUCKETS.map((g) => [g, a(`candidates.gaps.${g}`)])) as Record<
          GapBucket,
          string
        >,
        colMissing: a('candidates.x'),
        total: a('candidates.total'),
        unavailable: a('candidates.unavailable'),
      },
    };
  };

  /** Los nombres del anexo: solo si se marcó la casilla, por partes (tope del servidor). */
  const loadAnnex = async (input: AnalyticsExportInput) => {
    const people = new Map<string, PersonName>();
    for (const part of peopleRequests(annexPeopleIds(input.result))) {
      const res = await getAnalyticsPeople({ tenantId: currentTenantId!, personIds: part });
      for (const p of res.data.people) people.set(p.id, p);
    }
    // La lista «cerca y sin inscripción reciente» necesita la respuesta completa
    // de Candidatos: se pide aquí, solo porque se marcó la casilla.
    const full = await graduationCandidatesApi
      .list({ tenantId: currentTenantId! })
      .catch(() => null);
    return buildAnnex(input.result, full?.candidates ?? [], input.near, people, input.courses);
  };

  const download = async (
    kind: 'slides' | 'letter',
    input: AnalyticsExportInput,
    opts: { includeNames?: boolean } = {},
  ) => {
    setError(null);
    try {
      await openGeneratedPdf(
        async () => {
          const [{ pdf }, docs, brand] = await Promise.all([
            import('@react-pdf/renderer'),
            import('./AnalyticsPdf'),
            loadPdfBrand(currentTenantId!),
          ]);
          const L = labels(input, brand);
          // Las diapositivas NUNCA llevan nombres (reuniones y directores); el
          // informe carta, solo con la casilla marcada.
          const data =
            kind === 'letter' && opts.includeNames
              ? { ...input.data, annex: await loadAnnex(input) }
              : input.data;
          const Doc = kind === 'slides' ? docs.AnalyticsSlidesPdf : docs.AnalyticsLetterPdf;
          return renderWithoutHyphenation(() =>
            pdf(<Doc data={data} brand={brand} L={L} />).toBlob(),
          );
        },
        a(kind === 'slides' ? 'export.fileSlides' : 'export.fileLetter'),
      );
    } catch (e) {
      setError(errorText(e, a('export.error')));
    }
  };

  return { download, error };
}
